import { useRef, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import copy from 'copy-to-clipboard'
import { generateReference } from 'lib/references'
import OpenerIcon from 'components/misc/OpenerIcon'
import TypeIcon from 'components/misc/TypeIcon'
import Expand from 'components/misc/Expand'
import Tooltip from 'components/misc/Tooltip'
import Abstract from 'components/misc/Abstract'
import DropdownMenu from 'components/misc/DropdownMenu'
import { toggleModal, setCiteGeneratorPaperData } from 'store/ui/actions'
import Citation from './Citation'
import Paper from './Paper'
import styles from './styles/PaperCard.css'

const RESTRICTED_CITATIONS_COUNT = 1
const EXPANDED_CITATIONS_COUNT = 2

const handleCopyCite = ({ paper, citation, snippetRef }) => {
  const snippetText = snippetRef.current?.textContent || ''
  const reference = generateReference({
    reference: paper,
    citationStyle: 'apa'
  })

  copy(`
${citation.type} cite from section '${citation.section}' of publication:

${reference}

----------------------------------------------------------------
${snippetText}
----------------------------------------------------------------
`.trim().replace(/  +/, ' '))
}

const ExpandingCites = ({
  citations = [], expanded, snippetRef, textMatch, textMatches, linkTarget,
  paper, numCitesToShow, showClassification = true, showClickToCopy = true,
  truncateSnippets = false
}) => {
  const visibleCites = citations.slice(0, numCitesToShow)
  const expandingCites = citations.slice(numCitesToShow, citations.length)

  if (citations.length === 0) {
    return null
  }

  return (
    <div>
      <div>
        {visibleCites.map((citation, index) => (
          <Citation
            key={citation.id || index}
            textMatch={textMatch}
            textMatches={textMatches}
            citation={citation}
            paper={paper}
            snippetRef={snippetRef}
            linkTarget={linkTarget}
            handleCopy={() => handleCopyCite({ paper, citation, snippetRef })}
            showClassification={showClassification}
            showClickToCopy={showClickToCopy}
            truncateSnippet={truncateSnippets}
          />
        ))}
      </div>
      <Expand className={styles.expandingWrapper} show={expanded}>
        {expandingCites.map((citation, index) => (
          <Citation
            key={citation.id || index}
            textMatch={textMatch}
            textMatches={textMatches}
            citation={citation}
            paper={paper}
            snippetRef={snippetRef}
            linkTarget={linkTarget}
            handleCopy={() => handleCopyCite({ paper, citation, snippetRef })}
            showClassification={showClassification}
            showClickToCopy={showClickToCopy}
          />
        ))}
      </Expand>
    </div>
  )
}

export const PaperCard = ({
  citations, paper, textMatch, textMatches, tally, refNumber,
  linkTarget = false, restricted = false,
  forceExpand = false, showAbstract = true, card = true,
  showClassification = true, showMenu = true, showLinks = true,
  showClickToCopy = true, truncateSnippets = false,
  showCompactLinks = false
}) => {
  const dispatch = useDispatch()
  const user = useSelector(({ ui }) => ui.user)
  const snippetRef = useRef()
  const citationStatements = citations?.filter(citation => citation.id) || []
  const doiReferences = []
  const [expandCard, setExpandCard] = useState(forceExpand)
  const [abstractToggled, setAbstractToggled] = useState(showAbstract)

  const numCitesToShow = restricted ? RESTRICTED_CITATIONS_COUNT : EXPANDED_CITATIONS_COUNT
  const hiddenCitations = citationStatements.length - numCitesToShow

  const paperMenuItems = [
    {
      iconEl: <img alt='Generate Citation' src={`${CONFIG.assetsBaseURL}/images/icons/reports/download-csv.svg`} />,
      label: 'Generate Citation',
      handleClick: () => {
        dispatch(setCiteGeneratorPaperData({
          citeGeneratorPaperData: paper
        }))
        dispatch(toggleModal('generateCitation'))
      }
    }
  ]

  //
  // Expand abstract if we have a match there but not
  // in the citations
  //
  useEffect(() => {
    if (!textMatch) {
      return () => null
    }

    const allCiteText = citations.map(cite => cite.snippet?.toLowerCase() || '').join(' ')
    const abstract = paper?.abstract?.toLowerCase() || ''
    const lowerTextMatch = textMatch.toLowerCase()

    if (!allCiteText.includes(lowerTextMatch) && abstract.includes(lowerTextMatch)) {
      setAbstractToggled(true)
    }
  }, [textMatch, citations, paper])

  return (
    <div className={classNames(styles.paperCard, {
      [styles.card]: card
    })}
    >
      {refNumber && (
        <div className={styles.referenceNumber}>
          Reference #{refNumber}
        </div>
      )}
      <ExpandingCites
        citations={citations}
        expanded={expandCard}
        snippetRef={snippetRef}
        textMatch={textMatch}
        textMatches={textMatches}
        linkTarget={linkTarget}
        paper={paper}
        numCitesToShow={numCitesToShow}
        showClassification={showClassification}
        showClickToCopy={showClickToCopy}
        truncateSnippets={truncateSnippets}
      />
      {citations && citationStatements.length === 0 && doiReferences.length > 0 && (
        <div className={styles.typeInfo}>
          <Tooltip
            content={'We haven\'t accessed the full-text of this publication yet to extract and classify the relevant the citation statements. In the meantime, you can read its full-text by clicking view full text to find the citation statements yourself or you can click set alert to get notified when we have them.'}
          >
            <TypeIcon className={styles.icon} type='citation' />
            <span className={styles.citationType}>Citation</span>
            <i className={styles.infoIcon} />
          </Tooltip>
        </div>
      )}
      {citationStatements.length > numCitesToShow && (
        <span
          className={styles.expandCard}
          onClick={() => {
            if (restricted) {
              dispatch(toggleModal(user ? 'upgrade' : 'login', true))
              return
            }
            setExpandCard(!expandCard)
          }}
          role='button'
          tabIndex={0}
        >
          <OpenerIcon show={expandCard} />
          {!expandCard ? `See ${hiddenCitations} more ${hiddenCitations === 1 ? 'Smart Citation' : 'Smart Citations'}` : 'See fewer Smart Citations'}
        </span>
      )}
      <div className={showMenu && styles.grid}>
        <div className={styles.abstractWrapper}>
          {paper && (
            <Paper
              textMatch={textMatch}
              paper={paper}
              tally={tally}
              selfCites={citations?.[0]?.selfCites}
              showLinksLayout={showLinks}
              showCompactLinks={showCompactLinks}
            />
          )}
          <Expand show={!!(paper && abstractToggled)} width='auto'>
            <Abstract className={styles.abstract} abstract={paper?.abstract} textMatch={textMatch} blur={paper?.abstractHidden} />
          </Expand>
        </div>
        {showMenu && (
          <div className={styles.menu}>
            {!!paper?.abstract && (
              <a
                className={styles.textButton}
                tabIndex='0'
                onClick={() => setAbstractToggled(!abstractToggled)}
              >
                {abstractToggled ? 'hide abstract' : 'show abstract'}
              </a>
            )}
            <DropdownMenu items={paperMenuItems} />
          </div>
        )}
      </div>
    </div>
  )
}

export default PaperCard
